/**
 * Supplementary per-row data for list columns (GDN-2731).
 *
 * The generic list serializes each type's model, so a column can only show a
 * persisted field. Some of the most useful columns aren't persisted — the
 * transcription list's note count is derived by walking the transcript to its
 * encounter and unioning that with note-processing sessions.
 *
 * A type supplies an `enrich` component; the shell renders it around the
 * table with the ids on the current page. It fetches once for the page — not
 * once per row — and publishes the result here, where cells read it.
 *
 * Deliberately not a hook on the definition object: `enrich` is a component,
 * so React's own mount/unmount handles switching resource type, and the rules
 * of hooks aren't at the mercy of which type happens to be registered.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';

/** rowId → whatever that type's enrichment fetched for it. */
export type EnrichmentMap = Record<string, unknown>;

interface EnrichmentState {
  values: EnrichmentMap;
  isLoading: boolean;
}

const EnrichmentContext = createContext<EnrichmentState>({
  values: {},
  isLoading: false,
});

export function RowEnrichmentProvider({
  values,
  isLoading = false,
  children,
}: {
  values: EnrichmentMap;
  isLoading?: boolean;
  children: ReactNode;
}) {
  const state = useMemo(() => ({ values, isLoading }), [values, isLoading]);
  return (
    <EnrichmentContext.Provider value={state}>
      {children}
    </EnrichmentContext.Provider>
  );
}

/**
 * A row's enrichment, or undefined while it loads / when the type has none.
 *
 * `isLoading` is distinct from "loaded, but nothing for this row": a column
 * that renders 0 during the fetch would tell an operator a transcript
 * produced no notes when we simply don't know yet.
 */
export function useRowEnrichment<T = unknown>(
  rowId: string,
): { value: T | undefined; isLoading: boolean } {
  const { values, isLoading } = useContext(EnrichmentContext);
  return { value: values[rowId] as T | undefined, isLoading };
}

export interface ResourceEnrichmentProps {
  /** Ids of the rows currently rendered — the page, not the whole result set. */
  rowIds: string[];
  organizationId: string;
  children: ReactNode;
}
